import React, {FC, useCallback, useContext} from 'react'
import {useSelector} from 'react-redux'
import {apOfferMountTypes, OfferViewContext} from 'context/offerViewContext'
import {useTrackOfferView} from 'hooks/analytics'
import {useOfferClickHandler} from 'hooks/useOfferClickHandler'
import {useSplitBookingRedirectLink} from 'hooks/useSplitBookingRedirectLink'
import {getMetaBrandHasRooms} from 'modules/accommodation/selectors'
import {getSplitBookingDetails} from 'modules/sapiSearch/selectors'

import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'

interface Props {
  component: string
  hotelId: string
  hasInteraction?: boolean
}

export const SplitBookingOfferInteraction: FC<Props> = ({
  children,
  component,
  hotelId,
  hasInteraction = true
}) => {
  const offerView = useContext(OfferViewContext)
  const isEmbedded = useIsEmbeddedWithBrand()
  const {splitBookingOffers, splitBookingOfferUrl, splitBookingPosition} =
    useSelector(state => getSplitBookingDetails(state, hotelId))
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const isMountedOnAp = apOfferMountTypes.includes(offerView)

  const shouldScrollToRooms = brandHasRooms && isMountedOnAp

  const {offer} = splitBookingOffers?.[0] || {}

  const splitBookingRedirectLink = useSplitBookingRedirectLink({
    splitBookingOfferUrl,
    hotelId
  })

  const inViewRef = useTrackOfferView(offer, hotelId, component)
  const {handleClick: handleOfferClickEvents} = useOfferClickHandler({
    offerPosition: splitBookingPosition,
    isSplitBooking: true,
    component,
    hotelId,
    offer
  })

  const handleSBOfferClick = useCallback(
    (event: React.MouseEvent<Element, MouseEvent>) => {
      event.stopPropagation()
      handleOfferClickEvents(event)
      // Prevents both AP navigation and scroll to rooms to happen at the same time
      if (!shouldScrollToRooms) {
        localStorage.setItem(
          'navigationToAccommodationPage',
          Date.now().toString()
        )
        setTimeout(() => {
          window.open(splitBookingRedirectLink, isEmbedded ? '_self' : '_blank')
        }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
      }
    },
    [
      handleOfferClickEvents,
      shouldScrollToRooms,
      splitBookingRedirectLink,
      isEmbedded
    ]
  )

  if (!hasInteraction) return <>{children}</>

  return (
    <NoStyleLink ref={inViewRef} onClick={handleSBOfferClick}>
      {children}
    </NoStyleLink>
  )
}
